import * as React from "react";
import {
  Box,
  Paper,
  Stack,
  Avatar,
  Typography,
  Button,
  Divider,
  Switch,
} from "@mui/material";
import { useAuth, useAddTalent, useCurrentTalentProfile } from "../../../hooks";
import { toggleCoffeeCallAvailabilityFn } from "../../../services/api/talent.api";
import { useNavigate } from "react-router-dom";
import { enqueueSnackbar } from "notistack";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import LogoutIcon from "@mui/icons-material/Logout";
import SentimentDissatisfiedIcon from "@mui/icons-material/SentimentDissatisfied";
import {
  BusinessAccount,
  BusinessProfileInfo,
  Talent,
  User,
} from "../../../types/user";
import useCurrentGTP, { useGTPStore } from "../../../hooks/useGTP";
import { useState } from "react";
import CircularProgress from "@mui/material/CircularProgress";
import { useGetUserProfile } from "../../../services/api/user.api";
import GuestLiteModal from "../../../pages/Auth/GuestLiteModal";
import usebusinessInfo from "../../../hooks/useBusiness";
import useCurrentBusinessInfo from "../../../hooks/useCurrentBusiness";
import { useGetBusinessProfile } from "../../../services/api/business/business.api";

const TalentandUserToggleMenu = () => {
  const { userInfo, setUser } = useAuth();
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const { SetTalentProfile, currentProfile } = useCurrentTalentProfile();
  const { currentGTP, SetCurrentGTP } = useCurrentGTP();
  const navigate = useNavigate();
  const [isCoffeeCallActive, setIsCoffeeCallActive] = React.useState(
    !!currentProfile?.coffe_call_enabled
  );

  const isGuestLite = userInfo?.profile_completed === false;
  const [isGuestLiteModalOpen, setIsGuestLiteModalOpen] = useState(false);

  const { businessInfo, setBusinessInfo } = usebusinessInfo();
  const { currentBusiness, setCurrentBusiness } = useCurrentBusinessInfo();

  const allBusinessTalentsRef = React.useRef<BusinessProfileInfo[]>(
    businessInfo?.business_accounts ?? []
  );

  const allTalentsRef = React.useRef<Talent[]>(userInfo?.talent || []);
  const isCoffeeCallActiveRef = React.useRef<boolean>(isCoffeeCallActive);

  const { data: user, refetch } = useGetUserProfile({
    key: ["user-profile"],
    options: { enabled: false },
  });

  const { refetch: businessRefetch } = useGetBusinessProfile({
    key: ["get-business-profile"],
    options: { enabled: false },
  });

  React.useEffect(() => {
    allTalentsRef.current = userInfo?.talent || [];
  }, [userInfo]);

  React.useEffect(() => {
    allBusinessTalentsRef.current = businessInfo?.business_accounts || [];
  }, [businessInfo]);

  React.useEffect(() => {
    setIsCoffeeCallActive(!!currentProfile?.coffe_call_enabled);
    isCoffeeCallActiveRef.current = !!currentProfile?.coffe_call_enabled;
  }, [currentProfile]);

  const handleToggleCoffeeCall = async (checked: boolean) => {
    if (currentProfile) {
      try {
        await toggleCoffeeCallAvailabilityFn(currentProfile.id, {
          coffe_call_enabled: checked,
        });
        SetTalentProfile({ ...currentProfile, coffe_call_enabled: checked });
        isCoffeeCallActiveRef.current = checked;
        setIsCoffeeCallActive(checked);
        enqueueSnackbar("Coffee Call status updated", { variant: "success" });
      } catch (err) {
        enqueueSnackbar("Failed to update status", { variant: "error" });
      }
    }
  };

  const handleSignOut = () => {
    if (isLoggingOut) return;
    setIsLoggingOut(true);
    localStorage.setItem("isGuestLite", "false");
    enqueueSnackbar("Signing you out...", { variant: "info" });

    setTimeout(() => {
      try {
        localStorage.clear();
        sessionStorage.clear();
        enqueueSnackbar("Signed out successfully.", { variant: "success" });
        window.location.href = "/home";
      } catch (error) {
        enqueueSnackbar("Failed to sign out. Please try again.", {
          variant: "error",
        });
        setIsLoggingOut(false);
      }
    }, 600);
  };

  // —— Netflix-ish design tokens (local to this component)
  const COLORS = {
    bg: "#0b0b0b",
    panel: "#141414",
    panelHover: "#191919",
    border: "rgba(255,255,255,0.12)",
    text: "#ffffff",
    muted: "rgba(255,255,255,0.72)",
    soft: "rgba(255,255,255,0.06)",
    red: "#E50914",
    redHover: "#b20710",
    greenPill: "#166534",
  };

  const renderTalentAccounts = () => {
    const allTalents = allTalentsRef.current;
    if (!allTalents.length) {
      return (
        <Stack spacing={2} alignItems="center" textAlign="center" py={3}>
          <SentimentDissatisfiedIcon
            sx={{ fontSize: 42, color: COLORS.muted }}
          />
          <Typography fontWeight={700} sx={{ color: COLORS.text }}>
            No Talent Profiles Found
          </Typography>
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={
              isGuestLite
                ? () => setIsGuestLiteModalOpen(true)
                : () => navigate("/register-talent")
            }
            sx={{
              border: `2px solid ${COLORS.red}`,
              color: COLORS.text,
              fontWeight: 700,
              textTransform: "none",
              fontSize: 14,
              borderRadius: 2,
              px: 3,
              py: 1.1,
              backgroundColor: COLORS.soft,
              "&:hover": {
                borderColor: COLORS.redHover,
                backgroundColor: "#1f1f1f",
              },
            }}
          >
            Register as a Talent
          </Button>
        </Stack>
      );
    }

    return (
      <>
        {allTalents.map((talent) => {
          const selected = currentProfile?.id === talent.id;
          return (
            <Box
              key={talent.id}
              onClick={() => {
                SetTalentProfile(talent);
                SetCurrentGTP(null);
                navigate("/talent/profile");
                refetch();
                if (user) {
                  setUser(user.data);
                }
              }}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                px: 2,
                py: 1.5,
                borderRadius: 2,
                cursor: "pointer",
                backgroundColor: selected
                  ? "rgba(229,9,20,0.08)"
                  : "transparent",
                border: selected
                  ? `1px solid ${COLORS.border}`
                  : "1px solid transparent",
                "&:hover": { backgroundColor: COLORS.panelHover },
                transition: "background-color .2s, border-color .2s",
              }}
            >
              <Avatar
                src={talent.profile_image ?? ""}
                alt={talent.username}
                sx={{
                  width: 42,
                  height: 42,
                  bgcolor: COLORS.soft,
                  color: COLORS.text,
                  fontWeight: 700,
                }}
              >
                {talent.username?.[0]}
              </Avatar>
              <Box>
                <Typography
                  fontWeight={700}
                  fontSize={14}
                  sx={{ color: COLORS.text }}
                >
                  {talent.username}
                </Typography>
                <Box display="flex" alignItems="center" gap={1}>
                  <Box
                    sx={{
                      backgroundColor: COLORS.greenPill,
                      color: "#fff",
                      fontSize: 11,
                      px: 1.25,
                      py: 0.25,
                      borderRadius: "999px",
                      fontWeight: 600,
                    }}
                  >
                    Talent
                  </Box>
                  <Typography variant="caption" sx={{ color: COLORS.muted }}>
                    @{talent.username}
                  </Typography>
                </Box>
              </Box>
            </Box>
          );
        })}
        <Box display="flex" gap={2} mt={2} flexWrap="wrap">
          <Button
            fullWidth
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={
              isGuestLite
                ? () => setIsGuestLiteModalOpen(true)
                : () => navigate("/register-talent")
            }
            sx={{
              border: `2px solid ${COLORS.red}`,
              color: COLORS.text,
              fontWeight: 700,
              fontSize: 13,
              textTransform: "none",
              borderRadius: 2,
              py: 1.05,
              backgroundColor: COLORS.soft,
              "&:hover": {
                backgroundColor: COLORS.panelHover,
                borderColor: COLORS.redHover,
              },
            }}
          >
            Add Talent
          </Button>
          <GuestLiteModal
            open={isGuestLiteModalOpen}
            onClose={() => setIsGuestLiteModalOpen(false)}
          />
          {currentProfile && !currentGTP && (
            <Button
              fullWidth
              variant="outlined"
              startIcon={<EditIcon />}
              onClick={() => navigate("talent/profile/edit/:talentId")}
              sx={{
                border: `2px solid ${COLORS.border}`,
                color: COLORS.text,
                fontWeight: 700,
                fontSize: 13,
                textTransform: "none",
                borderRadius: 2,
                py: 1.05,
                backgroundColor: COLORS.soft,
                "&:hover": {
                  backgroundColor: COLORS.panelHover,
                  borderColor: COLORS.border,
                },
              }}
            >
              Edit
            </Button>
          )}
        </Box>
      </>
    );
  };

  const renderBusinessAccounts = () => {
    const allBusinessAccounts = allBusinessTalentsRef.current;
    if (!allBusinessAccounts.length) {
      return (
        <Stack spacing={2} alignItems="center" textAlign="center" py={3}>
          <SentimentDissatisfiedIcon
            sx={{ fontSize: 42, color: COLORS.muted }}
          />
          <Typography fontWeight={700} sx={{ color: COLORS.text }}>
            No Business Accounts Found
          </Typography>
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={
              isGuestLite
                ? () => setIsGuestLiteModalOpen(true)
                : () => navigate("/register-business-talent")
            }
            sx={{
              border: `2px solid ${COLORS.red}`,
              color: COLORS.text,
              fontWeight: 700,
              textTransform: "none",
              fontSize: 14,
              borderRadius: 2,
              px: 3,
              py: 1.1,
              backgroundColor: COLORS.soft,
              "&:hover": {
                borderColor: COLORS.redHover,
                backgroundColor: "#1f1f1f",
              },
            }}
          >
            Register Business
          </Button>
        </Stack>
      );
    }

    return (
      <>
        {allBusinessAccounts.map((account) => {
          const selected = currentBusiness?.id === account.id;
          return (
            <Box
              key={account.id}
              onClick={() => {
                setCurrentBusiness(account);
                SetCurrentGTP(null);
                businessRefetch().then((result) => {
                  if (result?.data?.data) {
                    setBusinessInfo(result.data.data);
                  }
                  navigate(`/TNPage`);
                });
              }}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                px: 2,
                py: 1.5,
                borderRadius: 2,
                cursor: "pointer",
                backgroundColor: selected
                  ? "rgba(229,9,20,0.08)"
                  : "transparent",
                border: selected
                  ? `1px solid ${COLORS.border}`
                  : "1px solid transparent",
                "&:hover": { backgroundColor: COLORS.panelHover },
                transition: "background-color .2s, border-color .2s",
              }}
            >
              <Avatar
                src={account.profile_image ?? ""}
                alt={account.business_name}
                sx={{
                  width: 42,
                  height: 42,
                  bgcolor: COLORS.soft,
                  color: COLORS.text,
                  fontWeight: 700,
                }}
              >
                {account.business_name?.[0]}
              </Avatar>
              <Box>
                <Typography
                  fontWeight={700}
                  fontSize={14}
                  sx={{ color: COLORS.text }}
                >
                  {account.business_name}
                </Typography>
                <Box display="flex" alignItems="center" gap={1}>
                  <Box
                    sx={{
                      backgroundColor: COLORS.greenPill,
                      color: "#fff",
                      fontSize: 11,
                      px: 1.25,
                      py: 0.25,
                      borderRadius: "999px",
                      fontWeight: 600,
                    }}
                  >
                    {account.business_type}
                  </Box>
                  <Typography variant="caption" sx={{ color: COLORS.muted }}>
                    @{account.username}
                  </Typography>
                </Box>
              </Box>
            </Box>
          );
        })}
      </>
    );
  };

  // const renderGTPAccounts = () => {
  //   const allGTPs = currentProfile?.gtps || [];

  //   return (
  //     <>
  //       {allGTPs?.map((gtp) => {
  //         return (
  //           <Box
  //             key={gtp.id}
  //             display="flex"
  //             alignItems="center"
  //             gap={2}
  //             onClick={() => {
  //               SetCurrentGTP(gtp);
  //               navigate("/GTP/profile");
  //             }}
  //             sx={{
  //               px: 2,
  //               py: 1.5,
  //               borderRadius: 2,
  //               cursor: "pointer",
  //               backgroundColor: "transparent",
  //               "&:hover": { backgroundColor: COLORS.panelHover },
  //               transition: "background-color .2s",
  //             }}
  //           >
  //             <Avatar
  //               src={gtp.profile_image ?? ""}
  //               sx={{
  //                 bgcolor: COLORS.soft,
  //                 width: 48,
  //                 height: 48,
  //                 color: COLORS.text,
  //                 fontWeight: 800,
  //               }}
  //             >
  //               {gtp.name?.[0]}
  //             </Avatar>
  //             <Box>
  //               <Typography fontWeight={800} sx={{ color: COLORS.text }}>
  //                 {gtp.name}
  //               </Typography>
  //               <Typography
  //                 component="span"
  //                 sx={{
  //                   backgroundColor: COLORS.greenPill,
  //                   color: "#fff",
  //                   fontSize: 11,
  //                   px: 1.5,
  //                   py: 0.3,
  //                   borderRadius: "999px",
  //                   fontWeight: 600,
  //                 }}
  //               >
  //                 GTP Account
  //               </Typography>
  //             </Box>
  //           </Box>
  //         );
  //       })}
  //     </>
  //   );
  // };

  return (
    <Paper
      sx={{
        p: { xs: 1.5, sm: 2 },
        borderRadius: 3,
        minWidth: { xs: "auto", sm: 340 },
        fontFamily: "Lexend",
        background:
          "linear-gradient(180deg, rgba(20,20,20,0.96), rgba(18,18,18,0.96))",
        color: COLORS.text,
        border: `1px solid ${COLORS.border}`,
        boxShadow: "0 18px 48px rgba(0,0,0,0.6)",
      }}
    >
      <Stack spacing={2}>
        {/* Main Account */}
        <Box
          display="flex"
          alignItems="center"
          gap={2}
          onClick={() => {
            setCurrentBusiness(null);
            SetTalentProfile(null);
            SetCurrentGTP(null);
            userInfo?.account_type === "Business" &&
              businessRefetch().then((result) => {
                if (result?.data?.data) {
                  setBusinessInfo(result.data.data);
                }
              });
            navigate("/user/dashboard");
          }}
          sx={{
            px: 2,
            py: 1.5,
            borderRadius: 2,
            cursor: "pointer",
            backgroundColor: COLORS.soft,
            "&:hover": { backgroundColor: COLORS.panelHover },
            transition: "background-color .2s",
          }}
        >
          <Avatar
            src={userInfo?.profile_image ?? ""}
            sx={{
              bgcolor: COLORS.soft,
              width: 48,
              height: 48,
              color: COLORS.text,
              fontWeight: 800,
            }}
          >
            U
          </Avatar>
          <Box minWidth={0}>
            <Typography fontWeight={800} noWrap sx={{ color: COLORS.text }}>
              {businessInfo ? businessInfo?.business_email : userInfo?.email}
            </Typography>
            <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
              <Typography
                component="span"
                sx={{
                  backgroundColor: COLORS.greenPill,
                  color: "#fff",
                  fontSize: 11,
                  px: 1.5,
                  py: 0.3,
                  borderRadius: "999px",
                  fontWeight: 600,
                }}
              >
                Main Account
              </Typography>
              <Typography variant="caption" sx={{ color: COLORS.muted }}>
                @{businessInfo ? businessInfo.username : userInfo?.username}
              </Typography>
            </Box>
          </Box>
        </Box>

        <Divider sx={{ borderColor: COLORS.border }} />

        {/* Talent accounts */}
        {userInfo?.account_type !== "Business" && renderTalentAccounts()}
        {userInfo?.account_type === "Business" && renderBusinessAccounts()}

        {/* Coffee Call toggle (only when talent selected) */}
        {/* {currentProfile && (
          <Box
            sx={{
              border: `1px dashed ${COLORS.border}`,
              borderRadius: 2,
              px: 2,
              py: 1.2,
              mt: 1,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              backgroundColor: COLORS.soft,
            }}
          >
            <Typography fontWeight={700} fontSize={14} sx={{ color: COLORS.text }}>
              Available for Coffee Call
            </Typography>
            <Switch
              checked={isCoffeeCallActiveRef.current}
              onChange={(e) => handleToggleCoffeeCall(e.target.checked)}
              sx={{
                "& .MuiSwitch-switchBase": {
                  color: COLORS.border,
                },
                "& .MuiSwitch-switchBase.Mui-checked": {
                  color: COLORS.red,
                },
                "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                  backgroundColor: COLORS.red,
                  opacity: 1,
                },
                "& .MuiSwitch-track": {
                  backgroundColor: "rgba(255,255,255,0.2)",
                },
              }}
            />
          </Box>
        )} */}

        {/* GTP Accounts */}
        {/* {renderGTPAccounts()} */}

        {/* Add GTP */}
        {/* {currentProfile && !currentGTP && (
          <Button
            fullWidth
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={() => navigate("/create-group-profile")}
            sx={{
              border: `2px solid ${COLORS.red}`,
              color: COLORS.text,
              fontWeight: 800,
              fontSize: 13,
              textTransform: "none",
              borderRadius: 2,
              py: 1.05,
              backgroundColor: COLORS.soft,
              "&:hover": {
                backgroundColor: COLORS.panelHover,
                borderColor: COLORS.redHover,
              },
            }}
          >
            Create Group Profile
          </Button>
        )} */}

        <Divider sx={{ borderColor: COLORS.border }} />

        {/* Logout */}
        <Button
          fullWidth
          startIcon={<LogoutIcon />}
          onClick={handleSignOut}
          disabled={isLoggingOut}
          sx={{
            mt: 1.5,
            fontFamily: "Lexend",
            borderRadius: 2,
            textTransform: "none",
            fontWeight: 800,
            fontSize: 14,
            justifyContent: "flex-start",
            color: COLORS.text,
            px: 2,
            py: 1.15,
            backgroundColor: COLORS.red,
            border: `1.5px solid ${COLORS.red}`,
            boxShadow: "0 12px 28px rgba(229,9,20,0.3)",
            "&:hover": {
              backgroundColor: COLORS.redHover,
              borderColor: COLORS.redHover,
            },
          }}
        >
          {isLoggingOut ? (
            <>
              <CircularProgress size={16} sx={{ color: "#fff", mr: 1 }} />
              Signing Out...
            </>
          ) : (
            "Logout"
          )}
        </Button>
      </Stack>
    </Paper>
  );
};

export default TalentandUserToggleMenu;
